import useSWR from 'swr'
import { Link } from 'react-router-dom'
import { fetcher } from '../api/client'

export default function DashboardPage() {
    const { data: prospects } = useSWR<any>('/api/prospects?pageSize=10', fetcher)
    const { data: leads } = useSWR<any>('/api/leads?pageSize=10', fetcher)
    const { data: ticklers } = useSWR<any>('/api/ticklers/mine', fetcher)
    const { data: emails } = useSWR<any>('/api/email-logs?pageSize=10', fetcher)

    return (
        <div>
            <h1>Dashboard</h1>
            <div className="split">
                <div className="card">
                    <h2>Top Leads</h2>
                    {!leads ? <p className="muted">Loading...</p> :
                        leads.items?.length === 0 ? <p className="muted">No leads yet — <Link to="/leads">rank some prospects</Link>.</p> :
                            <table>
                                <thead><tr><th>Score</th><th>Name</th><th>Status</th></tr></thead>
                                <tbody>
                                    {leads.items?.slice(0, 5).map((l: any) => (
                                        <tr key={l.id}>
                                            <td><span className={`score ${scoreClass(l.score)}`}>{l.score}</span></td>
                                            <td>{l.name} <span className="muted">— {l.company}</span></td>
                                            <td><StatusBadge status={l.status} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                    }
                    <Link to="/leads">All leads →</Link>
                </div>

                <div className="card">
                    <h2>Recent Prospects</h2>
                    {!prospects ? <p className="muted">Loading...</p> :
                        <ul style={{ paddingLeft: 16, margin: 0 }}>
                            {prospects.items?.slice(0, 5).map((p: any) => (
                                <li key={p.id} style={{ marginBottom: 6 }}>
                                    <Link to={`/prospects/${p.id}`}>{p.name}</Link>
                                    <span className="muted"> — {p.company}</span>
                                </li>
                            ))}
                        </ul>
                    }
                    <Link to="/prospects">All prospects →</Link>
                </div>

                <div className="card">
                    <h2>Open Ticklers</h2>
                    {!ticklers ? <p className="muted">Loading...</p> :
                        ticklers.length === 0 ? <p className="muted">Nothing on your plate today.</p> :
                            <ul style={{ paddingLeft: 16, margin: 0 }}>
                                {ticklers.slice(0, 5).map((t: any) => (
                                    <li key={t.id} style={{ marginBottom: 6 }}>
                                        <strong>{new Date(t.dueDate).toLocaleDateString()}</strong>
                                        <span className="muted"> — {t.eventType}: {t.notes ?? '(no notes)'}</span>
                                    </li>
                                ))}
                            </ul>
                    }
                    <Link to="/ticklers">All ticklers →</Link>
                </div>

                <div className="card">
                    <h2>Recent Emails</h2>
                    {!emails ? <p className="muted">Loading...</p> :
                        emails.items?.length === 0 ? <p className="muted">No emails sent yet.</p> :
                            <ul style={{ paddingLeft: 16, margin: 0 }}>
                                {emails.items?.slice(0, 5).map((e: any) => (
                                    <li key={e.id} style={{ marginBottom: 6 }}>
                                        <strong>{e.subject}</strong>
                                        <span className="muted"> → {e.emailTo}</span>
                                        <span className="muted"> {e.openCount > 0 ? `· ${e.openCount} opens` : ''}{e.clickCount > 0 ? ` · ${e.clickCount} clicks` : ''}</span>
                                    </li>
                                ))}
                            </ul>
                    }
                    <Link to="/email-logs">All emails →</Link>
                </div>
            </div>
        </div>
    )
}

function scoreClass(s: number) {
    if (s >= 80) return 'score-high'
    if (s >= 60) return 'score-mid'
    return 'score-low'
}

/* API may return enum as int (legacy) or string (after JsonStringEnumConverter).
 * Map both cases to a stable label. */
const STATUS_LABELS = ['New', 'Contacted', 'Interested', 'Qualified', 'Rejected', 'Converted'] as const

export function StatusBadge({ status }: { status: string | number }) {
    const label = typeof status === 'number' ? (STATUS_LABELS[status] ?? 'Unknown') : status
    const cls = `badge badge-${String(label).toLowerCase()}`
    return <span className={cls}>{label}</span>
}
